<script lang="ts" setup>
  // Validation occurs:
  // - Validate on Change/Blur initially (when the user leaves the control), let’s call this lazy mode.
  // - If the field is invalid, switch the validation to validate on input (when the user types), let’s call this aggressive mode.
  // - If the field is valid, go back to “lazy” mode, otherwise, be “aggressive”.
  // See https://vee-validate.logaretm.com/v4/guide/composition-api/custom-inputs#handling-events

  import type Textarea from 'primevue/textarea'
  import { camelCaseToKebabCase } from '~/utils/string'

  const props = withDefaults(
    defineProps<{
      autoResize?: boolean
      disabled?: boolean
      label: string
      helperText?: string
      name: string
      maxlength?: number
      rows?: number
      validateInitialValue?: boolean
    }>(),
    {
      autoResize: true,
      disabled: false,
      helperText: undefined,
      maxlength: 254,
      rows: 4
    }
  )

  const emits = defineEmits<{
    (event: 'blur', payload: FocusEvent): void
  }>()

  const inputElement = ref<InstanceType<typeof Textarea> | null>(null)
  defineExpose({ inputElement })

  const selectorName = computed(() => camelCaseToKebabCase(props.name))

  const { value, errorMessage, handleBlur, validate, meta } = useField<string | null | undefined>(
    () => props.name,
    undefined,
    {
      validateOnValueUpdate: true
    }
  )

  onMounted(() => {
    // If there is an initial value on mount, and the field is not validated, validate it.
    if (props.validateInitialValue && !(value.value === undefined || value.value === null) && !meta.validated) {
      validate()
    }
  })

  function onBlur(event: FocusEvent) {
    emits('blur', event)
    handleBlur(event, true)
  }
</script>

<template>
  <div class="c-textarea-field">
    <FloatLabel variant="in">
      <Textarea
        :id="selectorName"
        ref="inputElement"
        v-model="value"
        :auto-resize="autoResize"
        :disabled
        :invalid="!!errorMessage"
        :maxlength
        :rows
        fluid
        @blur="onBlur"
      />
      <label :class="{ 'invalid': !!errorMessage }" :for="selectorName">{{ label }}</label>
    </FloatLabel>
    <small v-if="errorMessage" :id="`${selectorName}-error`" class="input-error-message">
      {{ errorMessage }}
    </small>
    <small v-else-if="helperText" class="input-helper">{{ helperText }}</small>
  </div>
</template>

<style lang="scss" scoped>
  @import '../styles/form.scss';
</style>
